import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CalendarClock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const ymd = (d) => d.toISOString().slice(0, 10);
const MODES = [
  ['forgot_in', 'Forgot Clock-In'],
  ['forgot_out', 'Forgot Clock-Out'],
  ['both', 'Both'],
];

export const ReportMissingTimeModal = ({ token, open, onClose, onSubmitted }) => {
  const { toast } = useToast();
  const today = ymd(new Date());
  const minDate = ymd(new Date(Date.now() - 14 * 86400000));
  const [form, setForm] = useState({ date: today, mode: 'both', clock_in: '', clock_out: '', job_id: 'none', reason: '' });
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!open) return;
    setForm({ date: today, mode: 'both', clock_in: '', clock_out: '', job_id: 'none', reason: '' });
    axios.get(`${API}/worker/pickable-jobs`, auth)
      .then(({ data }) => setJobs((data.jobs || []).slice(0, 200)))
      .catch(() => setJobs([]));
  }, [open]); // eslint-disable-line

  const needsIn = form.mode === 'forgot_in' || form.mode === 'both';
  const needsOut = form.mode === 'forgot_out' || form.mode === 'both';
  const reasonLen = form.reason.trim().length;

  const submit = async () => {
    if (needsIn && !form.clock_in) {
      toast({ title: 'Enter the clock-in time', variant: 'destructive' });
      return;
    }
    if (needsOut && !form.clock_out) {
      toast({ title: 'Enter the clock-out time', variant: 'destructive' });
      return;
    }
    if (reasonLen < 10) {
      toast({ title: 'Please explain what happened (at least 10 characters)', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/time/amendments`, {
        job_id: form.job_id === 'none' ? null : form.job_id,
        requested_clock_in: needsIn ? new Date(`${form.date}T${form.clock_in}`).toISOString() : null,
        requested_clock_out: needsOut ? new Date(`${form.date}T${form.clock_out}`).toISOString() : null,
        reason: form.reason.trim(),
      }, auth);
      toast({ title: 'Missing time reported', description: 'Your supervisor has been notified.' });
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast({ title: 'Could not submit request', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="report-missing-time-dialog">
        <DialogHeader>
          <DialogTitle>Report Missing Time</DialogTitle>
          <DialogDescription>Forgot to punch? Tell your supervisor and they'll fix your timesheet.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.date} max={today} min={minDate}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="missing-time-date-input" />
          </div>
          <div>
            <Label className="text-xs">What happened?</Label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="missing-time-mode-selector">
              {MODES.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, mode: v })}
                  className={`p-2 rounded-lg border text-xs font-semibold transition-colors ${
                    form.mode === v
                      ? 'border-[#00C896] bg-[#0F1923] text-[#00C896]'
                      : 'border-[#2D3748] bg-[#0F1923] text-slate-400'
                  }`}
                  data-testid={`missing-time-mode-${v}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {needsIn && (
            <div>
              <Label className="text-xs">Clock-in time</Label>
              <Input type="time" value={form.clock_in}
                onChange={(e) => setForm({ ...form, clock_in: e.target.value })}
                data-testid="missing-time-clockin-input" />
            </div>
          )}
          {needsOut && (
            <div>
              <Label className="text-xs">Clock-out time</Label>
              <Input type="time" value={form.clock_out}
                onChange={(e) => setForm({ ...form, clock_out: e.target.value })}
                data-testid="missing-time-clockout-input" />
            </div>
          )}
          <div>
            <Label className="text-xs">Job (optional)</Label>
            <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
              <SelectTrigger data-testid="missing-time-job-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No specific job</SelectItem>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} — ` : ''}{j.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reason (required)</Label>
            <Textarea value={form.reason} rows={3}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Forgot my phone in the truck and couldn't clock in on site"
              data-testid="missing-time-reason-input" />
            <p className={`text-[11px] mt-1 text-right ${reasonLen >= 10 ? 'text-slate-500' : 'text-amber-400'}`} data-testid="missing-time-reason-count">
              {reasonLen}/10 characters minimum
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="missing-time-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="missing-time-submit-btn">
            {busy ? 'Sending…' : 'Submit Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReportMissingTimeButton = ({ token, onSubmitted, className = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className={`w-full ht-card p-4 flex items-center gap-3 text-left hover:border-blue-500/30 transition-all ${className}`}
        data-testid="report-missing-time-btn">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,160,0,0.12)' }}>
          <CalendarClock className="w-5 h-5" style={{ color: '#FFA000' }} />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">Report Missing Time</p>
          <p className="text-xs" style={{ color: '#64748B' }}>Forgot to clock in or out? Request a fix</p>
        </div>
      </button>
      {open && (
        <ReportMissingTimeModal token={token} open={open}
          onClose={() => setOpen(false)} onSubmitted={onSubmitted} />
      )}
    </>
  );
};

export default ReportMissingTimeButton;
